/**
 * Pure geometry for the annotation tools (unit tested): client → page transforms, hit testing,
 * bounds, highlighter snapping, glyph centring, and move/scale of annotation data.
 *
 * Spaces:
 *  - normalised: 0..1 of page width (x) / height (y) — the viewer's PagePoint space;
 *  - quantised:  integers 0..Q — what annotation data stores;
 *  - page units: x 0..1000, y 0..1000·h/w — the <PageSvg> viewBox, uniform in both axes, so
 *    distances and angles are measured here.
 */
import {
  CONNECTOR_SIZE_RANGE,
  DEFAULT_CONNECTOR_SIZE,
  Q,
  STAMP_SIZE_RANGE,
  TEXT_SIZE_LIMITS,
  type AnnotationObject,
  type AnnotationOf,
  type DataByKind,
} from '../../shared/annotations';
import type { PageSize } from '../../shared/dto/library';
import { SMUFL_BY_NAME, type SmuflGlyph } from '../../shared/smufl';
import { MIN_VISIBLE_PIN_PX, pinDiameterPx } from '../viewer/pinSize';

/** Page width in page units (same as PAGE_UNITS_W of viewer/PageSvg). */
export const UNITS_W = 1000;
/** Hit tolerance around objects, in CSS pixels. */
export const HIT_TOLERANCE_PX = 12;
/** Padding of the selection frame around an object (CSS px); a tap inside the frame is on the selection. */
export const SELECTION_PAD_PX = 8;
export const TEXT_LINE_HEIGHT = 1.2;
/** Average glyph advance of the UI font as a fraction of the font size (bbox estimate). */
export const TEXT_CHAR_WIDTH = 0.56;
/** First baseline below the text's top edge, as a fraction of the font size. */
export const TEXT_ASCENT = 0.86;

export interface Pt {
  x: number;
  y: number;
}
export interface Rect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}
export interface ClientRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/** Page height in page units (mirrors viewer/PageSvg pageUnitsHeight). */
export function pageHeightUnits(page: PageSize | undefined): number {
  return page && page.w > 0 && page.h > 0 ? (UNITS_W * page.h) / page.w : UNITS_W * Math.SQRT2;
}

/* ─── Coordinate transforms ─────────────────────────────────────────────── */

/** Client point → normalised point of a page whose box has the given client rect (clamped to the page). */
export function clientToPage(rect: ClientRect, clientX: number, clientY: number, clampToPage = true): Pt {
  const x = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
  const y = rect.height > 0 ? (clientY - rect.top) / rect.height : 0;
  return clampToPage ? { x: clamp(x, 0, 1), y: clamp(y, 0, 1) } : { x, y };
}

/** Normalised page point → client point. */
export function pageToClient(rect: ClientRect, x: number, y: number): { clientX: number; clientY: number } {
  return { clientX: rect.left + x * rect.width, clientY: rect.top + y * rect.height };
}

/** CSS pixels → page units for a page box currently `boxWidthCss` pixels wide. */
export function cssToUnits(px: number, boxWidthCss: number): number {
  return boxWidthCss > 0 ? (px * UNITS_W) / boxWidthCss : px;
}

/** Quantised size (1/Q of page width) → page units. */
export const sizeUnits = (v: number): number => (v / Q) * UNITS_W;
/** Quantised point → page units. */
export const qToUnits = (qx: number, qy: number, H: number): Pt => ({ x: (qx / Q) * UNITS_W, y: (qy / Q) * H });
/** Normalised point → page units. */
export const normToUnits = (p: Pt, H: number): Pt => ({ x: p.x * UNITS_W, y: p.y * H });

/* ─── Distances ─────────────────────────────────────────────────────────── */

export function distToSegment(p: Pt, a: Pt, b: Pt): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 > 0 ? clamp(((p.x - a.x) * dx + (p.y - a.y) * dy) / len2, 0, 1) : 0;
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

function orient(a: Pt, b: Pt, c: Pt): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

export function segmentsIntersect(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const o1 = orient(a, b, c);
  const o2 = orient(a, b, d);
  const o3 = orient(c, d, a);
  const o4 = orient(c, d, b);
  return o1 * o2 < 0 && o3 * o4 < 0;
}

/** Shortest distance between segments ab and cd (0 when they cross). */
export function segmentsDistance(a: Pt, b: Pt, c: Pt, d: Pt): number {
  if (segmentsIntersect(a, b, c, d)) return 0;
  return Math.min(distToSegment(a, c, d), distToSegment(b, c, d), distToSegment(c, a, b), distToSegment(d, a, b));
}

export function rectContains(r: Rect, p: Pt, pad = 0): boolean {
  return p.x >= r.x0 - pad && p.x <= r.x1 + pad && p.y >= r.y0 - pad && p.y <= r.y1 + pad;
}

export function rectsOverlap(a: Rect, b: Rect, pad = 0): boolean {
  return a.x0 - pad <= b.x1 && b.x0 - pad <= a.x1 && a.y0 - pad <= b.y1 && b.y0 - pad <= a.y1;
}

/** Does segment ab touch rect r (grown by pad)? */
export function segmentHitsRect(a: Pt, b: Pt, r: Rect, pad = 0): boolean {
  const g: Rect = { x0: r.x0 - pad, y0: r.y0 - pad, x1: r.x1 + pad, y1: r.y1 + pad };
  if (rectContains(g, a) || rectContains(g, b)) return true;
  const c1 = { x: g.x0, y: g.y0 };
  const c2 = { x: g.x1, y: g.y0 };
  const c3 = { x: g.x1, y: g.y1 };
  const c4 = { x: g.x0, y: g.y1 };
  return segmentsIntersect(a, b, c1, c2) || segmentsIntersect(a, b, c2, c3) || segmentsIntersect(a, b, c3, c4) || segmentsIntersect(a, b, c4, c1);
}

/* ─── Glyphs & text ─────────────────────────────────────────────────────── */

/**
 * Text origin (baseline-left) so that a SMuFL glyph's bounding box is centred on (cx, cy).
 * `em` is the font size in the target units; bboxes are in staff spaces (1 em = 4 staff spaces, y up).
 */
export function glyphOrigin(g: Pick<SmuflGlyph, 'sw' | 'ne'>, em: number, cx: number, cy: number): Pt {
  const s = em / 4;
  return { x: cx - ((g.sw[0] + g.ne[0]) / 2) * s, y: cy + ((g.sw[1] + g.ne[1]) / 2) * s };
}

/** The glyph's box (y down) when centred on (cx, cy). */
export function glyphBox(g: Pick<SmuflGlyph, 'sw' | 'ne'>, em: number, cx: number, cy: number): Rect {
  const s = em / 4;
  const w = (g.ne[0] - g.sw[0]) * s;
  const h = (g.ne[1] - g.sw[1]) * s;
  return { x0: cx - w / 2, y0: cy - h / 2, x1: cx + w / 2, y1: cy + h / 2 };
}

/** Font size that fits a glyph's box into a square of `box` units (used by the palette buttons). */
export function glyphFitSize(g: Pick<SmuflGlyph, 'sw' | 'ne'>, box: number, maxEm: number): number {
  const w = (g.ne[0] - g.sw[0]) / 4;
  const h = (g.ne[1] - g.sw[1]) / 4;
  const ext = Math.max(w, h, 0.01);
  return Math.min(maxEm, box / ext);
}

export function textLines(text: string): string[] {
  return text.split(/\r?\n/);
}

/** Estimated text box (units) for a font size `fs` (units). */
export function textBox(text: string, fs: number): { w: number; h: number } {
  const lines = textLines(text);
  const longest = Math.max(1, ...lines.map((l) => [...l].length));
  return { w: longest * fs * TEXT_CHAR_WIDTH, h: lines.length * fs * TEXT_LINE_HEIGHT };
}

/* ─── Bounds & hit testing (page units) ─────────────────────────────────── */

export interface HitContext {
  /** Page height in units. */
  H: number;
  /** Smallest drawn connector pin radius in units (viewer/pinSize.ts MIN_VISIBLE_PIN_PX / 2 at the current zoom). */
  minPinR: number;
}

/** Hit context of a page `H` units high whose box is `boxWidthCss` pixels wide. */
export function hitContext(H: number, boxWidthCss: number): HitContext {
  return { H, minPinR: cssToUnits(MIN_VISIBLE_PIN_PX / 2, boxWidthCss) };
}

/** Radius (units) of a connector's pin as drawn: its own size, at least the visible minimum (viewer/pinSize.ts pinDiameterPx). */
export function pinRadiusUnits(o: AnnotationOf<'connector'>, ctx: HitContext): number {
  return Math.max(sizeUnits(o.data.size ?? DEFAULT_CONNECTOR_SIZE) / 2, ctx.minPinR);
}

function inkPoint(pts: readonly number[], i: number, H: number): Pt {
  return { x: (pts[i * 3]! / Q) * UNITS_W, y: (pts[i * 3 + 1]! / Q) * H };
}

/** Visual bounds of an object in page units (stroke widths included). */
export function objectBounds(o: AnnotationObject, ctx: HitContext): Rect {
  const { H } = ctx;
  switch (o.kind) {
    case 'highlight': {
      const a = qToUnits(o.data.a[0], o.data.a[1], H);
      const b = qToUnits(o.data.b[0], o.data.b[1], H);
      const r = sizeUnits(o.data.width) / 2;
      return { x0: Math.min(a.x, b.x) - r, y0: Math.min(a.y, b.y) - r, x1: Math.max(a.x, b.x) + r, y1: Math.max(a.y, b.y) + r };
    }
    case 'ink': {
      const pts = o.data.pts;
      const n = pts.length / 3;
      let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
      for (let i = 0; i < n; i++) {
        const p = inkPoint(pts, i, H);
        if (p.x < x0) x0 = p.x;
        if (p.y < y0) y0 = p.y;
        if (p.x > x1) x1 = p.x;
        if (p.y > y1) y1 = p.y;
      }
      const r = sizeUnits(o.data.width) * 0.8;
      return { x0: x0 - r, y0: y0 - r, x1: x1 + r, y1: y1 + r };
    }
    case 'text': {
      const at = qToUnits(o.data.at[0], o.data.at[1], H);
      const { w, h } = textBox(o.data.text, sizeUnits(o.data.size));
      return { x0: at.x, y0: at.y, x1: at.x + w, y1: at.y + h };
    }
    case 'stamp': {
      const at = qToUnits(o.data.at[0], o.data.at[1], H);
      const g = SMUFL_BY_NAME.get(o.data.glyph);
      const em = sizeUnits(o.data.size);
      return g ? glyphBox(g, em, at.x, at.y) : { x0: at.x - em / 4, y0: at.y - em / 4, x1: at.x + em / 4, y1: at.y + em / 4 };
    }
    case 'connector': {
      const at = qToUnits(o.data.at[0], o.data.at[1], H);
      const r = pinRadiusUnits(o, ctx);
      return { x0: at.x - r, y0: at.y - r, x1: at.x + r, y1: at.y + r };
    }
  }
}

/* ─── Selection hit areas (CSS px) ──────────────────────────────────────── */

/** Smallest touch target (CSS px) of the selection's move area and its two handles. */
export const HANDLE_TARGET_PX = 44;
/** Radius (CSS px) of a handle's visible icon: a tap there leaves everything as it is. */
export const HANDLE_ICON_RADIUS_PX = 16;
/** Handle centre ↔ bounds corner (CSS px, per axis) along a side of at least HANDLE_TARGET_PX: just outside the frame. */
export const HANDLE_NEAR_PX = SELECTION_PAD_PX + 6;
/** …along a shorter side: far enough that the handle's target never reaches the frame (2 px to spare). */
export const HANDLE_CLEAR_PX = HANDLE_TARGET_PX / 2 + SELECTION_PAD_PX + 2;

/**
 * Offset (CSS px, per axis, outwards) of the move grip (top-left) and the resize handle
 * (bottom-right) from the object's bounds corners, for bounds of `widthPx` × `heightPx`. Along a side
 * shorter than the target the handles are pushed out so their 44 px targets never cover the object
 * or its frame (a press on a small symbol or a one-line text always moves it); on larger objects they
 * sit just outside the frame corners and reach at most 8 px into the object's corners.
 */
export function handleOffsetPx(widthPx: number, heightPx: number): Pt {
  return {
    x: widthPx < HANDLE_TARGET_PX ? HANDLE_CLEAR_PX : HANDLE_NEAR_PX,
    y: heightPx < HANDLE_TARGET_PX ? HANDLE_CLEAR_PX : HANDLE_NEAR_PX,
  };
}

/** Part of the selection's hit areas under a point (CSS px), as PageOverlay stacks them: resize handle over grip over move area. */
export type SelectionHit = 'scale' | 'grip' | 'move' | null;

/**
 * Which hit area of a selection with bounds `b` (CSS px; handle offsets from its committed size
 * `basePx`, default b's own size) is at `p`. Mirrors PageOverlay's SelectionHandles layout.
 */
export function selectionHitAt(p: Pt, b: Rect, scalable: boolean, basePx?: { w: number; h: number }): SelectionHit {
  const half = HANDLE_TARGET_PX / 2;
  const inSquare = (cx: number, cy: number) => Math.abs(p.x - cx) <= half && Math.abs(p.y - cy) <= half;
  if (scalable) {
    const off = handleOffsetPx(basePx?.w ?? b.x1 - b.x0, basePx?.h ?? b.y1 - b.y0);
    if (inSquare(b.x1 + off.x, b.y1 + off.y)) return 'scale';
    if (inSquare(b.x0 - off.x, b.y0 - off.y)) return 'grip';
  }
  const hw = Math.max(HANDLE_TARGET_PX, b.x1 - b.x0) / 2;
  const hh = Math.max(HANDLE_TARGET_PX, b.y1 - b.y0) / 2;
  const cx = (b.x0 + b.x1) / 2;
  const cy = (b.y0 + b.y1) / 2;
  return Math.abs(p.x - cx) <= hw && Math.abs(p.y - cy) <= hh ? 'move' : null;
}

/** Is page-unit point p on (or within tol of) the object? */
export function hitObject(o: AnnotationObject, p: Pt, tol: number, ctx: HitContext): boolean {
  const { H } = ctx;
  switch (o.kind) {
    case 'highlight':
      return distToSegment(p, qToUnits(o.data.a[0], o.data.a[1], H), qToUnits(o.data.b[0], o.data.b[1], H)) <= sizeUnits(o.data.width) / 2 + tol;
    case 'ink': {
      if (!rectContains(objectBounds(o, ctx), p, tol)) return false;
      const pts = o.data.pts;
      const n = pts.length / 3;
      const lim = sizeUnits(o.data.width) / 2 + tol;
      if (n === 1) return Math.hypot(p.x - inkPoint(pts, 0, H).x, p.y - inkPoint(pts, 0, H).y) <= lim;
      for (let i = 1; i < n; i++) if (distToSegment(p, inkPoint(pts, i - 1, H), inkPoint(pts, i, H)) <= lim) return true;
      return false;
    }
    case 'text':
    case 'stamp':
      return rectContains(objectBounds(o, ctx), p, tol);
    case 'connector': {
      const at = qToUnits(o.data.at[0], o.data.at[1], H);
      return Math.hypot(p.x - at.x, p.y - at.y) <= pinRadiusUnits(o, ctx) + tol;
    }
  }
}

/** Does the eraser segment ab (page units) touch the object? */
export function segmentHitsObject(o: AnnotationObject, a: Pt, b: Pt, tol: number, ctx: HitContext): boolean {
  const { H } = ctx;
  const seg: Rect = { x0: Math.min(a.x, b.x), y0: Math.min(a.y, b.y), x1: Math.max(a.x, b.x), y1: Math.max(a.y, b.y) };
  const bounds = objectBounds(o, ctx);
  if (!rectsOverlap(seg, bounds, tol)) return false;
  switch (o.kind) {
    case 'highlight':
      return segmentsDistance(a, b, qToUnits(o.data.a[0], o.data.a[1], H), qToUnits(o.data.b[0], o.data.b[1], H)) <= sizeUnits(o.data.width) / 2 + tol;
    case 'ink': {
      const pts = o.data.pts;
      const n = pts.length / 3;
      const lim = sizeUnits(o.data.width) / 2 + tol;
      if (n === 1) return distToSegment(inkPoint(pts, 0, H), a, b) <= lim;
      for (let i = 1; i < n; i++) if (segmentsDistance(a, b, inkPoint(pts, i - 1, H), inkPoint(pts, i, H)) <= lim) return true;
      return false;
    }
    case 'text':
    case 'stamp':
      return segmentHitsRect(a, b, bounds, tol);
    case 'connector':
      return distToSegment(qToUnits(o.data.at[0], o.data.at[1], H), a, b) <= pinRadiusUnits(o, ctx) + tol;
  }
}

/** Top-most object (last in z-order) hit at p that passes `accept`. */
export function topmostHit(
  objects: readonly AnnotationObject[],
  p: Pt,
  tol: number,
  ctx: HitContext,
  accept: (o: AnnotationObject) => boolean = () => true,
): AnnotationObject | null {
  for (let i = objects.length - 1; i >= 0; i--) {
    const o = objects[i]!;
    if (accept(o) && hitObject(o, p, tol, ctx)) return o;
  }
  return null;
}

/* ─── Highlighter ───────────────────────────────────────────────────────── */

/**
 * Snaps the end point of a highlighter segment to horizontal when the segment is within ±maxDeg of
 * horizontal. Points are normalised; `aspect` = page height / width (angles measured on the page).
 */
export function snapHorizontal(a: Pt, b: Pt, aspect: number, maxDeg = 8): Pt {
  const dx = b.x - a.x;
  const dy = (b.y - a.y) * aspect;
  if (dx === 0 && dy === 0) return b;
  const deg = (Math.atan2(Math.abs(dy), Math.abs(dx)) * 180) / Math.PI;
  return deg <= maxDeg ? { x: b.x, y: a.y } : b;
}

/* ─── Moving & scaling annotation data (quantised units) ────────────────── */

type Q2 = [number, number];
const qc = (v: number): number => clamp(Math.round(v), 0, Q);

/** Anchor-point bounds (quantised) of the stored geometry. */
export function anchorBoundsQ(o: AnnotationObject): Rect {
  const xs: number[] = [];
  const ys: number[] = [];
  const add = (p: readonly number[]) => {
    xs.push(p[0]!);
    ys.push(p[1]!);
  };
  switch (o.kind) {
    case 'highlight':
      add(o.data.a);
      add(o.data.b);
      break;
    case 'ink':
      for (let i = 0; i < o.data.pts.length; i += 3) add([o.data.pts[i]!, o.data.pts[i + 1]!]);
      break;
    case 'text':
    case 'stamp':
    case 'connector':
      add(o.data.at);
      break;
  }
  return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
}

/** Limits a move (quantised) so the object's anchors stay on the page. */
export function clampDelta(o: AnnotationObject, dx: number, dy: number): { dx: number; dy: number } {
  const b = anchorBoundsQ(o);
  return { dx: clamp(dx, -b.x0, Q - b.x1), dy: clamp(dy, -b.y0, Q - b.y1) };
}

/** Moves an object's data by (dx, dy) quantised units (connector targets stay where they are). */
export function translateData(o: AnnotationObject, dx: number, dy: number): DataByKind[AnnotationObject['kind']] {
  const mv = (p: readonly number[]): Q2 => [qc(p[0]! + dx), qc(p[1]! + dy)];
  switch (o.kind) {
    case 'highlight':
      return { ...o.data, a: mv(o.data.a), b: mv(o.data.b) };
    case 'ink': {
      const pts = o.data.pts.slice();
      for (let i = 0; i < pts.length; i += 3) {
        pts[i] = qc(pts[i]! + dx);
        pts[i + 1] = qc(pts[i + 1]! + dy);
      }
      return { ...o.data, pts };
    }
    case 'text':
      return { ...o.data, at: mv(o.data.at) };
    case 'stamp':
      return { ...o.data, at: mv(o.data.at) };
    case 'connector':
      return { ...o.data, at: mv(o.data.at) };
  }
}

/** Text / symbol / connector pin size limits of the model (1/Q of page width). */
export { TEXT_SIZE_LIMITS };
export const STAMP_SIZE_LIMITS = STAMP_SIZE_RANGE;
export const MIN_SCALE = 0.2;
export const MAX_SCALE = 6;
/** Factor of one "A−" / "A+" step of a text, symbol or connector pin. */
export const SIZE_STEP = 1.15;

/** Objects with a size of their own (A− / A+): texts, symbols and connector pins. */
export type SizedKind = 'text' | 'stamp' | 'connector';
export type SizedObject = AnnotationOf<SizedKind>;

export function isSized(o: AnnotationObject): o is SizedObject {
  return o.kind === 'text' || o.kind === 'stamp' || o.kind === 'connector';
}

/** Size (1/Q of page width) of a text, symbol or connector pin; a connector without one has the default. */
export function sizeOf(o: SizedObject): number {
  return o.kind === 'connector' ? (o.data.size ?? DEFAULT_CONNECTOR_SIZE) : o.data.size;
}

export function sizeLimits(kind: SizedKind): readonly [number, number] {
  return kind === 'text' ? TEXT_SIZE_LIMITS : kind === 'stamp' ? STAMP_SIZE_LIMITS : CONNECTOR_SIZE_RANGE;
}

/**
 * Text / symbol / pin size one "A+" (dir 1) or "A−" (dir −1) step away, within the model's limits.
 * A pin on a page box `boxW` px wide steps from its size as drawn (viewer/pinSize.ts: at least
 * MIN_VISIBLE_PIN_PX), like the corner handle (scaledPinSize), so every press shows: a default pin
 * drawn at the minimum (portrait, two-up) grows at once, and A− on a pin at the minimum changes nothing.
 */
export function stepSize(kind: SizedKind, size: number, dir: 1 | -1, boxW?: number): number {
  const [lo, hi] = sizeLimits(kind);
  const minDrawn = kind === 'connector' && boxW && boxW > 0 ? (Q * MIN_VISIBLE_PIN_PX) / boxW : 0;
  if (dir < 0 && size <= minDrawn) return size;
  const from = Math.max(size, minDrawn);
  const next = dir > 0 ? Math.max(size + 1, Math.round(from * SIZE_STEP)) : Math.min(size - 1, Math.round(size / SIZE_STEP));
  return clamp(next, lo, hi);
}

/**
 * A connector pin's size after a resize by `k` on a page box `boxW` CSS px wide. The handle measures
 * the pin as drawn (viewer/pinSize.ts pinDiameterPx: at least MIN_VISIBLE_PIN_PX), so `k` applies to
 * that size, not to a smaller stored one; a shrink that stays at the visible minimum keeps the size.
 * Without `boxW` it scales the stored size. Clamped to CONNECTOR_SIZE_RANGE.
 */
export function scaledPinSize(o: AnnotationOf<'connector'>, k: number, boxW?: number): number {
  const size = sizeOf(o);
  const kk = clamp(Number.isFinite(k) ? k : 1, MIN_SCALE, MAX_SCALE);
  if (kk === 1) return size;
  const drawn = boxW && boxW > 0 ? Math.max(size, (Q * MIN_VISIBLE_PIN_PX) / boxW) : size;
  const next = clamp(Math.round(drawn * kk), CONNECTOR_SIZE_RANGE[0], CONNECTOR_SIZE_RANGE[1]);
  return kk < 1 ? Math.min(next, size) : next;
}

/**
 * Bounds (page units) of a connector's pin centred on `c` as a resize by `k` lands on a page box
 * `boxW` px wide: the pin as drawn then (never below the visible minimum), for the resize frame.
 */
export function scaledPinBounds(o: AnnotationOf<'connector'>, c: Pt, k: number, boxW: number): Rect {
  const r = cssToUnits(pinDiameterPx(scaledPinSize(o, k, boxW), boxW) / 2, boxW);
  return { x0: c.x - r, y0: c.y - r, x1: c.x + r, y1: c.y + r };
}

/**
 * Scale factor after the kind's size limits are applied (text / symbol / pin sizes are clamped; a
 * pin's from its size as drawn on a page box `boxW` px wide, see scaledPinSize).
 */
export function effectiveScale(o: AnnotationObject, k: number, boxW?: number): number {
  const kk = clamp(Number.isFinite(k) ? k : 1, MIN_SCALE, MAX_SCALE);
  if (o.kind === 'connector') return scaledPinSize(o, kk, boxW) / sizeOf(o);
  if (isSized(o)) {
    const [lo, hi] = sizeLimits(o.kind);
    const size = sizeOf(o);
    return clamp(Math.round(size * kk), lo, hi) / size;
  }
  return kk;
}

/**
 * Scales an object about `origin` (quantised). Text/stamp change size; ink/highlight their geometry;
 * a connector only its pin's size (about the pin's centre: `at` and the target never move; from its
 * size as drawn on a page box `boxW` px wide, see scaledPinSize).
 */
export function scaleData(o: AnnotationObject, origin: Q2, k: number, boxW?: number): DataByKind[AnnotationObject['kind']] {
  if (o.kind === 'connector') return { ...o.data, size: scaledPinSize(o, k, boxW) };
  const kk = effectiveScale(o, k);
  const sc = (p: readonly number[]): Q2 => [qc(origin[0] + (p[0]! - origin[0]) * kk), qc(origin[1] + (p[1]! - origin[1]) * kk)];
  switch (o.kind) {
    case 'highlight':
      return { ...o.data, a: sc(o.data.a), b: sc(o.data.b) };
    case 'ink': {
      const pts = o.data.pts.slice();
      for (let i = 0; i < pts.length; i += 3) {
        const [x, y] = sc([pts[i]!, pts[i + 1]!]);
        pts[i] = x;
        pts[i + 1] = y;
      }
      return { ...o.data, pts };
    }
    case 'text':
      return { ...o.data, at: sc(o.data.at), size: Math.round(o.data.size * kk) };
    case 'stamp':
      return { ...o.data, at: sc(o.data.at), size: Math.round(o.data.size * kk) };
  }
}

/**
 * Scale factor for a corner-handle drag: projection of the pointer onto the origin→handle diagonal
 * (page units), so the handle follows the pointer along the box's diagonal.
 */
export function dragScale(origin: Pt, handle: Pt, pointer: Pt): number {
  const hx = handle.x - origin.x;
  const hy = handle.y - origin.y;
  const len2 = hx * hx + hy * hy;
  if (len2 <= 0) return 1;
  return clamp(((pointer.x - origin.x) * hx + (pointer.y - origin.y) * hy) / len2, MIN_SCALE, MAX_SCALE);
}
